/**
 * galeria3/obraz-w-przegladarce.js — obraz obrabiany W PRZEGLĄDARCE klienta, zanim cokolwiek
 * poleci na serwer (Photo Match przez przeglądarkę, 2026-09-07; wzorzec z wtyczki:
 * `MULTI_PLATFORM_EXTENSION/background.js` → `normalizePhotosForAllegro`).
 *
 * Leży w galeria3/ (nie shared/), bo strażnik świeżości paczki (`tests/test_paczki_studia_swieze.py`)
 * wymaga, żeby każde źródło z mapy esbuilda mieszkało w katalogu Studia.
 *
 * Trzy rzeczy, wszystkie bez bibliotek:
 *   miniaturka(file, px)     — kwadrat px×px z białymi pasami (to samo „pad do kwadratu", które
 *                              robi serwer w `compute_hashes`), JPEG ~12 KB dla 256 px. Zmniejszanie
 *                              schodkowe (połowienie) — jednorazowe zmniejszenie 4000→256 px daje
 *                              w przeglądarce widoczny aliasing i psuje odcisk.
 *   zmniejsz(file, maxBok)   — pełny obraz z dłuższym bokiem ≤ maxBok, JPEG q0.9, orientacja
 *                              z EXIF już zastosowana (serwer i tak robi `exif_transpose`).
 *   exifDataCzas(file)       — `DateTimeOriginal` (+ `SubSecTimeOriginal`) z nagłówka JPEG jako
 *                              sekundy uniksowe (czas lokalny, jak `datetime.strptime().timestamp()`
 *                              po stronie serwera). Brak EXIF → null. Czyta tylko pierwsze 256 KB.
 *
 * ⚠️ HEIC: `createImageBitmap` poza Safari go nie dekoduje — takie pliki rzucają, wołający
 * je liczy i mówi klientowi „tylko JPG". Dziś tak samo jest na dysku operatora.
 */

const ROZSZERZENIA_JPG = /\.(jpe?g)$/i;

export function jestJpg(file) {
  return ROZSZERZENIA_JPG.test(file.name || '') || file.type === 'image/jpeg';
}

async function _bitmapa(file) {
  // `from-image` = zastosuj obrót z EXIF; serwer liczy odcisk po `exif_transpose`, więc
  // miniaturka musi być obrócona tak samo — inaczej odcisk portretu z telefonu się rozjedzie.
  return await createImageBitmap(file, { imageOrientation: 'from-image' });
}

function _plotno(w, h) {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}

async function _doBlob(plotno, jakosc) {
  if (plotno.convertToBlob) return await plotno.convertToBlob({ type: 'image/jpeg', quality: jakosc });
  return await new Promise((res, rej) => plotno.toBlob(b => b ? res(b) : rej(new Error('toBlob')), 'image/jpeg', jakosc));
}

/** Schodkowe zmniejszenie bitmapy do (w, h) — połowienie aż do ≤ 2× celu, potem jeden krok. */
function _zmniejszSchodkowo(bmp, w, h) {
  let zrodlo = bmp;
  let sw = bmp.width, sh = bmp.height;
  while (sw / 2 >= w && sh / 2 >= h) {
    const nw = Math.round(sw / 2), nh = Math.round(sh / 2);
    const c = _plotno(nw, nh);
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(zrodlo, 0, 0, nw, nh);
    if (zrodlo !== bmp && zrodlo.close) zrodlo.close();
    zrodlo = c; sw = nw; sh = nh;
  }
  return { zrodlo, sw, sh };
}

export async function miniaturka(file, px = 256, jakosc = 0.85) {
  const bmp = await _bitmapa(file);
  const szer = bmp.width, wys = bmp.height;
  const bok = Math.max(szer, wys);
  const skala = px / bok;
  const w = Math.max(1, Math.round(szer * skala)), h = Math.max(1, Math.round(wys * skala));
  const { zrodlo } = _zmniejszSchodkowo(bmp, w, h);
  const c = _plotno(px, px);
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, px, px);
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(zrodlo, Math.floor((px - w) / 2), Math.floor((px - h) / 2), w, h);
  bmp.close();
  const blob = await _doBlob(c, jakosc);
  return { blob, width: szer, height: wys };
}

export async function zmniejsz(file, maxBok = 2000, jakosc = 0.9) {
  const bmp = await _bitmapa(file);
  const szer = bmp.width, wys = bmp.height;
  const bok = Math.max(szer, wys);
  const skala = Math.min(1, maxBok / bok);
  const w = Math.max(1, Math.round(szer * skala)), h = Math.max(1, Math.round(wys * skala));
  const { zrodlo } = _zmniejszSchodkowo(bmp, w, h);
  const c = _plotno(w, h);
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(zrodlo, 0, 0, w, h);
  bmp.close();
  const blob = await _doBlob(c, jakosc);
  return { blob, width: w, height: h };
}

/** `DateTimeOriginal` z EXIF → sekundy uniksowe (czas lokalny) albo null. */
export async function exifDataCzas(file) {
  let dv;
  try {
    dv = new DataView(await file.slice(0, 256 * 1024).arrayBuffer());
  } catch { return null; }
  if (dv.byteLength < 4 || dv.getUint16(0) !== 0xFFD8) return null;
  let off = 2;
  while (off + 4 <= dv.byteLength) {
    const marker = dv.getUint16(off);
    if (marker === 0xFFDA || (marker & 0xFF00) !== 0xFF00) break;   // dane obrazu / śmieć
    const len = dv.getUint16(off + 2);
    if (marker === 0xFFE1 && off + 10 <= dv.byteLength && dv.getUint32(off + 4) === 0x45786966) {  // "Exif"
      return _czasZTiff(dv, off + 10);
    }
    off += 2 + len;
  }
  return null;
}

function _czasZTiff(dv, tiff) {
  try {
    const little = dv.getUint16(tiff) === 0x4949;
    const g16 = p => dv.getUint16(p, little), g32 = p => dv.getUint32(p, little);
    const ifd0 = tiff + g32(tiff + 4);
    const n = g16(ifd0);
    let exifIfd = null;
    for (let i = 0; i < n; i++) {
      const e = ifd0 + 2 + i * 12;
      if (e + 12 > dv.byteLength) return null;
      if (g16(e) === 0x8769) { exifIfd = tiff + g32(e + 8); break; }
    }
    if (exifIfd == null || exifIfd + 2 > dv.byteLength) return null;
    const m = g16(exifIfd);
    let dt = null, sub = null;
    const ascii = (e) => {
      const cnt = g32(e + 4);
      const ptr = cnt > 4 ? tiff + g32(e + 8) : e + 8;
      let s = '';
      for (let k = 0; k < cnt - 1 && ptr + k < dv.byteLength; k++) s += String.fromCharCode(dv.getUint8(ptr + k));
      return s;
    };
    for (let i = 0; i < m; i++) {
      const e = exifIfd + 2 + i * 12;
      if (e + 12 > dv.byteLength) break;
      const tag = g16(e);
      if (tag === 0x9003) dt = ascii(e);          // DateTimeOriginal
      else if (tag === 0x9291) sub = ascii(e);    // SubSecTimeOriginal
    }
    if (!dt) return null;
    const mm = dt.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
    if (!mm) return null;
    const t = new Date(+mm[1], +mm[2] - 1, +mm[3], +mm[4], +mm[5], +mm[6]).getTime() / 1000;
    if (!isFinite(t)) return null;
    const ulamek = sub ? (parseFloat('0.' + sub.trim()) || 0) : 0;
    return t + ulamek;
  } catch { return null; }
}

/** Kolejka z ograniczoną współbieżnością — telefon nie zdekoduje 5000 zdjęć naraz. */
export async function poKolei(elementy, naraz, praca, naPostep) {
  let i = 0, gotowe = 0;
  const wyniki = new Array(elementy.length);
  async function pracownik() {
    while (i < elementy.length) {
      const idx = i++;
      try { wyniki[idx] = { ok: true, wynik: await praca(elementy[idx], idx) }; }
      catch (e) { wyniki[idx] = { ok: false, blad: e }; }
      gotowe++;
      if (naPostep) naPostep(gotowe, elementy.length);
    }
  }
  await Promise.all(Array.from({ length: Math.max(1, naraz) }, pracownik));
  return wyniki;
}

/* ── pamięć odcisków w przeglądarce (IndexedDB) — klucz: ścieżka|rozmiar|data pliku ── */
const DB_NAZWA = 'lf-photo-match', SKLEP = 'odciski';

function _db() {
  return new Promise((res, rej) => {
    if (typeof indexedDB === 'undefined') return rej(new Error('brak IndexedDB'));
    const r = indexedDB.open(DB_NAZWA, 1);
    r.onupgradeneeded = () => { r.result.createObjectStore(SKLEP, { keyPath: 'klucz' }); };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

export function kluczPliku(file) {
  return `${file.webkitRelativePath || file.name}|${file.size}|${file.lastModified}`;
}

export async function odciskiZPamieci(klucze) {
  try {
    const db = await _db();
    return await new Promise((res) => {
      const tx = db.transaction(SKLEP, 'readonly'); const st = tx.objectStore(SKLEP);
      const out = new Map();
      let n = 0;
      if (klucze.length === 0) return res(out);
      for (const k of klucze) {
        const q = st.get(k);
        q.onsuccess = () => { if (q.result) out.set(k, q.result); if (++n === klucze.length) res(out); };
        q.onerror = () => { if (++n === klucze.length) res(out); };
      }
    });
  } catch { return new Map(); }
}

export async function zapamietajOdciski(wpisy) {
  try {
    const db = await _db();
    await new Promise((res, rej) => {
      const tx = db.transaction(SKLEP, 'readwrite'); const st = tx.objectStore(SKLEP);
      for (const w of wpisy) st.put(w);
      tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error);
    });
  } catch { /* pamięć podręczna — brak nie psuje przebiegu */ }
}
